// api/assessmentDrafts.ts
//
// Phase 2C, Stage 6 - client for the encrypted, server-side assessment
// draft endpoints (design §9, resolving CLIENT-08). Replaces the previous
// plaintext browser localStorage drafts used by AssessmentPart1.tsx and
// AssessmentPart2.tsx.
import api from '../utils/api';

export type AssessmentDraftPart = 'part1' | 'part2';

const BASE_URL = '/api/assessment-drafts';

export async function fetchAssessmentDraft<T>(clientId: string, part: AssessmentDraftPart): Promise<{ content: T; updatedAt: string } | null> {
  try {
    const response = await api.get(`${BASE_URL}/${clientId}/${part}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function saveAssessmentDraft<T>(clientId: string, part: AssessmentDraftPart, content: T): Promise<void> {
  await api.put(`${BASE_URL}/${clientId}/${part}`, { content });
}

export async function deleteAssessmentDraft(clientId: string, part: AssessmentDraftPart): Promise<void> {
  try {
    await api.delete(`${BASE_URL}/${clientId}/${part}`);
  } catch (error) {
    // Best-effort - if this fails, the draft will simply expire via its
    // 7-day server-side TTL instead of being removed immediately.
    console.error('Error deleting assessment draft:', error);
  }
}

/**
 * Best-effort removal of any legacy plaintext localStorage drafts left
 * over from before Stage 6. Safe to call repeatedly (e.g. on every app
 * bootstrap and every logout) - a no-op once nothing matches.
 */
export function clearLegacyLocalStorageDrafts(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /^assessment-part[12]-draft-/.test(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing legacy assessment drafts from localStorage:', error);
  }
}
